import { fabric } from 'fabric';
import { overrideSplitTextIntoLines } from './textbox-width';
import { enableLongWordWrapping } from './textbox-wrapping';
import {
  handleTextboxBehaviorOnSelection,
  setupControls,
  textboxControlsVisibility,
} from './textbox-scaling';
import { updateMixedFontSizesState } from './textbox-styles';

export const TEXT_CONSTANTS = {
  MIN_FONT_SIZE: 10,
  MAX_FONT_SIZE: 100,
  DEFAULT_PADDING: 10,
  EPSILON: 0.0001,
  MAX_TEXTBOX_WIDTH: 600,
  WRAP_WIDTH_MARGIN: 0.1,
  INITIAL_TEXTBOX_WIDTH: 5,
  OLD_INITIAL_TEXTBOX_WIDTH: 320,
} as const;

let customTextboxBehaviorEnabled = false;

export function setCustomTextboxBehavior(enabled: boolean): void {
  customTextboxBehaviorEnabled = enabled;
}

export function isCustomTextboxBehaviorEnabled(): boolean {
  return customTextboxBehaviorEnabled;
}

export function initialize(): void {
  overrideToObjectForText();
  overrideSplitTextIntoLines();
  overrideExitEditing();
  enableLongWordWrapping();
  setupControls();
  handleTextboxBehaviorOnSelection();
}

/**
 * override Textbox toObject for now,
 * there are some bugs and issues with stylesToArray function that is used in Fabric 5
 * it doesn't compare styles as expected, also there are some bugs on it when using undefined object
 * check the issues here for reference
 * https://github.com/fabricjs/fabric.js/pull/8357
 * https://github.com/fabricjs/fabric.js/pull/8365
 * @param propertiesToInclude
 */
const overrideToObjectForText = function (): void {
  fabric.Text.prototype.toObject = function (propertiesToInclude: string[]) {
    const additionalProps = (
      'fontFamily fontWeight fontSize text underline minWidth overline linethrough' +
      ' textAlign fontStyle lineHeight textBackgroundColor charSpacing styles' +
      ' direction path pathStartOffset pathSide pathAlign splitByGrapheme padding maxWidth resized hasMixedSizes'
    ).split(' ');
    const allProperties = additionalProps.concat(propertiesToInclude);
    const obj = fabric.Object.prototype.toObject.call(this, allProperties);
    obj.styles = (fabric.util.object as any).clone((this as any).styles, true);
    return obj;
  };
};

const overrideExitEditing = function (): void {
  /**
   * Override of fabric.IText.prototype.exitEditing
   *
   * Original implementation:
   * @see https://github.com/fabricjs/fabric.js/blob/v5.3.0/src/mixins/itext_behavior.mixin.ts#L1042
   *
   * changes:
   * - skip firing object:modified event in some cases to not force exit editing on remote side
   * - fix this issue: https://pncl.atlassian.net/browse/SPAC-13626
   */
  (fabric.IText.prototype as any).exitEditing = function (skipFireObjectModifiedEvent = false) {
    const isTextChanged = this._textBeforeEdit !== this.text;
    const hiddenTextarea = this.hiddenTextarea;
    this.selected = false;
    this.isEditing = false;

    this.selectionEnd = this.selectionStart;

    if (hiddenTextarea) {
      hiddenTextarea.blur && hiddenTextarea.blur();
      hiddenTextarea.parentNode && hiddenTextarea.parentNode.removeChild(hiddenTextarea);
    }
    this.hiddenTextarea = null;
    this.abortCursorAnimation();
    this._restoreEditingProps();
    if (this._shouldClearDimensionCache()) {
      this.initDimensions();
      this.setCoords();
    }
    this.fire('editing:exited');
    isTextChanged && this.fire('modified');
    if (this.canvas) {
      this.canvas.off('mouse:move', this.mouseMoveHandler);
      this.canvas.fire('text:editing:exited', { target: this });
      !skipFireObjectModifiedEvent &&
        isTextChanged &&
        this.canvas.fire('object:modified', { target: this });
    }
    return this;
  };
};

// for new created textboxes
export function setCustomTextboxProperties(textbox: fabric.Textbox): void {
  if (isCustomTextboxBehaviorEnabled()) {
    (textbox as any).set('maxWidth', TEXT_CONSTANTS.MAX_TEXTBOX_WIDTH); // the max width of a textbox
    (textbox as any).set('resized', false); // true when textbox is resized manually from the controls, initially it is false
  } else {
    (textbox as any).set('maxWidth', TEXT_CONSTANTS.OLD_INITIAL_TEXTBOX_WIDTH); // textbox fixed width value
    (textbox as any).set('resized', true); // true to treat textbox as fixed width
  }
  (textbox as any).set('hasMixedSizes', false); // true when textbox has different font sizes, initially it is false
}

/**
 * this is needed to update some properties for old textboxes (textboxes created before the new textbox behavior was introduced)
 * to make them behave like the new textboxes
 */
export function updateOldTextboxProperties(textbox: fabric.Textbox): void {
  if (!textbox) {
    return;
  }
  textbox.set('padding', TEXT_CONSTANTS.DEFAULT_PADDING);
  // to reset scale of old textboxes that were resized in a multi-selection before the new implementation was introduced
  textbox.set('scaleX', 1);
  textbox.set('scaleY', 1);
  textbox.setControlsVisibility(textboxControlsVisibility);
  if ((textbox as any).hasMixedSizes === undefined) {
    updateMixedFontSizesState(textbox as fabric.Textbox);
  }
  if ((textbox as any).resized === undefined) {
    // set resized to true to treat old textboxes as fixed width
    (textbox as any).resized = true;
    (textbox as any).maxWidth = textbox.width;
  }
}

export function isInputArea(activeElement: Element | null): boolean {
  const nodeName = activeElement?.nodeName;
  return (
    nodeName === 'TEXTAREA' ||
    nodeName === 'INPUT' ||
    (nodeName === 'DIV' && activeElement?.parentElement?.id === 'advanced-text-fragment')
  );
}

export function setTextboxCachingLimits(
  canvas: fabric.Canvas,
  width: number,
  height: number,
): void {
  (canvas as any)._textboxCachingLimits = { width, height };
}
